import { useEffect, useRef, useState } from 'react';
import { allesNachziehen } from './frisch.ts';
import {
  TAKT_MS, beschaeftigt, neueVersion, sollNachziehen, zugangGeaendert, type Pulsstand,
} from './puls.ts';
import { OBERFLAECHE } from './Stand.tsx';
import { versionsnummer } from './version.ts';

/**
 * Der Puls als Bauteil: fragt, zieht nach, und zeigt den Balken, wenn eine
 * neue Version eingespielt ist. Die Regeln stehen in `puls.ts`.
 *
 * DER BALKEN ERSETZT DEN ALTEN VERSIONSHINWEIS. Der sah nur beim Öffnen der
 * Seite nach und sagte dann „Strg und F5". Dieser hier merkt es auch, wenn
 * die App den ganzen Tag offen ist - genau der Fall der installierten
 * Chrome-App - und lädt mit einem Klick neu.
 *
 * DIE BENACHRICHTIGUNG AM RECHNER kommt nur, wenn sie erlaubt ist. Fragen
 * darf der Browser erst nach einem Klick; deshalb steht im Balken „am Rechner
 * melden", solange weder erlaubt noch abgelehnt ist.
 */
export function Puls({ wasIstNeu }: { wasIstNeu?: () => void }) {
  const bekannt = useRef<number | null>(null);
  const wartet = useRef(false);
  const zugang = useRef<string | null>(null);
  const gemeldet = useRef<string | null>(null);
  const [server, setServer] = useState<string | null>(null);
  const [weg, setWeg] = useState<string | null>(null);
  const [erlaubnis, setErlaubnis] = useState<string>(
    typeof Notification === 'undefined' ? 'nicht_da' : Notification.permission);

  useEffect(() => {
    let aus = false;
    let uhr: ReturnType<typeof setTimeout> | null = null;

    const nachziehen = () => {
      if (aus) return;
      if (beschaeftigt(document)) {
        /* Später noch einmal: wer tippt, verliert nichts. */
        wartet.current = true;
        setTimeout(nachziehen, 1_500);
        return;
      }
      wartet.current = false;
      /* DIE ROLLSTELLE BLEIBT (.335).
         ORIGINALTEXT MARVIN (08.10.2026)
           „auf dem Handy, wenn ich dort eine PDF öffne, kriege ich die zwar
            ganz normal geöffnet, dann geht er aber beim Schließen hin, lädt
            die Seite komplett neu und springt dann auch wieder nach oben."
         Die PDF geht in einem eigenen Reiter auf; beim Zurückkommen ist der
         Reiter wieder sichtbar, der Puls fragt nach und zieht alles nach, was
         inzwischen geschah. Die Bildschirme laden dann neu, und ein
         Bildschirm, der kurz leer ist, steht danach oben. Hier wird die
         Rollstelle vorher gemerkt und so lange wiederhergestellt, bis die
         Seite wieder so hoch ist wie vorher. */
      const buehne = document.querySelector('.buehne') as HTMLElement | null;
      const vorher = buehne ? buehne.scrollTop : window.scrollY;
      allesNachziehen();
      if (vorher > 0) {
        const bis = Date.now() + 1500;
        const zurueck = () => {
          const jetzt = buehne ? buehne.scrollTop : window.scrollY;
          if (jetzt < vorher) { if (buehne) buehne.scrollTop = vorher; else window.scrollTo({ top: vorher }); }
          if (Date.now() < bis) requestAnimationFrame(zurueck);
        };
        requestAnimationFrame(zurueck);
      }
    };

    /* IM HINTERGRUND SELTENER UND NUR NACH DER VERSION. Die installierte
       App liegt oft stundenlang verdeckt; gerade dann soll die Meldung
       „neue Version" am Rechner kommen. Nachgezogen wird erst beim
       Zurückkommen - einmal, und dann mit allem, was inzwischen geschah. */
    const fragen = async () => {
      if (aus) return;
      const verdeckt = document.visibilityState === 'hidden';
      try {
        const a = await fetch('/api/puls', { credentials: 'same-origin', cache: 'no-store' });
        if (a.ok) {
          const d = (await a.json()) as Pulsstand;
          if (d.version) setServer(d.version);
          /* .583: Rechte oder Menü geändert: App lädt Menü und Rechte neu. */
          if (zugangGeaendert(zugang.current, d.zugang)) window.dispatchEvent(new Event('mds-zugang'));
          if (d.zugang) zugang.current = d.zugang;
          if (!verdeckt) {
            if (sollNachziehen(bekannt.current, d.aenderung) && !wartet.current) nachziehen();
            if (d.aenderung !== null && d.aenderung !== undefined) bekannt.current = d.aenderung;
          }
        }
      } catch { /* der nächste Takt fragt wieder */ }
      plane();
    };
    const plane = () => {
      if (aus) return;
      if (uhr) clearTimeout(uhr);
      uhr = setTimeout(fragen, document.visibilityState === 'hidden' ? 60_000 : TAKT_MS);
    };
    const sichtbar = () => { if (document.visibilityState === 'visible') void fragen(); };

    void fragen();
    document.addEventListener('visibilitychange', sichtbar);
    return () => {
      aus = true;
      if (uhr) clearTimeout(uhr);
      document.removeEventListener('visibilitychange', sichtbar);
    };
  }, []);

  const neu = neueVersion(OBERFLAECHE, server);

  /* EINMAL JE VERSION AM RECHNER MELDEN, wenn erlaubt. */
  useEffect(() => {
    if (!neu || !server || gemeldet.current === server) return;
    gemeldet.current = server;
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        const n = new Notification('MDS FTTH: neue Version', {
          body: `Version ${versionsnummer(server)} ist eingespielt. Zum Neuladen klicken.`,
          tag: 'mds-version',
        });
        n.onclick = () => { window.focus(); window.location.reload(); };
      } catch { /* manche Umgebungen erlauben es nur über einen Service Worker */ }
    }
  }, [neu, server]);

  if (!neu || weg === server) return null;
  return (
    <div className="uebernahmebalken versionsbalken" role="status">
      <span>
        <b>Neue Version {versionsnummer(server)}</b> ist eingespielt. Diese
        Seite läuft noch mit {versionsnummer(OBERFLAECHE)}.
      </span>
      <span className="reihe" style={{ gap: 8 }}>
        {wasIstNeu && (
          <button className="knopf" onClick={wasIstNeu}>Was ist neu</button>
        )}
        {erlaubnis === 'default' && (
          <button className="knopf"
                  onClick={() => Notification.requestPermission()
                    .then((p) => setErlaubnis(p)).catch(() => {})}>
            künftig am Rechner melden
          </button>
        )}
        <button className="knopf haupt" onClick={() => window.location.reload()}>
          Jetzt neu laden
        </button>
        <button className="knopf" onClick={() => setWeg(server)}>später</button>
      </span>
    </div>
  );
}

/**
 * „Am Rechner melden" erlauben - einmal, unten in der Seitenleiste. Ohne
 * diese Erlaubnis kann die installierte App im Hintergrund nichts melden.
 * Steht nur da, solange weder erlaubt noch abgelehnt ist.
 */
export function RechnerMelden() {
  const [p, setP] = useState<string>(
    typeof Notification === 'undefined' ? 'nicht_da' : Notification.permission);
  if (p !== 'default') return null;
  return (
    <button type="button" className="alslink klein"
            style={{ display: 'block', marginTop: 6 }}
            onClick={() => Notification.requestPermission()
              .then((x) => setP(x)).catch(() => {})}>
      Neue Versionen am Rechner melden
    </button>
  );
}
